import { useCallback, useEffect, useRef, useState } from 'react';
import type { ViewportInfo } from '../../hooks/useFabricCanvas';
import { pickTickSpacing, tickPositions, visibleSceneRange } from '../../lib/ruler-ticks';

/** Width of the left ruler / height of the top ruler in CSS
 *  pixels. Photoshop uses ~20px; we pick a slightly wider 22 so
 *  the longest label ("10000") fits without truncation. */
export const RULER_THICKNESS = 22;

interface RulersProps {
  viewport: ViewportInfo;
  /** Subscribe to scene-space cursor moves. Rulers manage their
   *  own cursor state internally to avoid bubbling 60+ Hz state
   *  updates up to EditorPage (which has many children). Same
   *  pattern StatusBar uses. */
  subscribeToCursorMove: (cb: (x: number, y: number) => void) => () => void;
  /** Fired on mouseup AFTER dragging from a ruler — the parent
   *  dispatches `ADD_GUIDE` with the resulting scene-space
   *  coordinate. `axis` matches the source ruler. The position
   *  is in scene-space, computed from the drop-point screen
   *  coords via the viewport transform. */
  onSpawnGuide: (axis: 'horizontal' | 'vertical', position: number) => void;
}

/** Top + left ruler chrome rendered as canvas overlays. Sized to
 *  the viewport; tick density follows zoom (see
 *  `lib/ruler-ticks::pickTickSpacing`). Drag from a ruler into
 *  the canvas to spawn a new guide; the actual guide creation
 *  fires on mouseup via `onSpawnGuide`.
 *
 *  Pointer events are enabled ONLY on the ruler strips
 *  themselves (the L-shaped chrome). The center canvas area
 *  beneath is left alone — Fabric's own handlers stay in
 *  control. */
export function Rulers({ viewport, subscribeToCursorMove, onSpawnGuide }: RulersProps) {
  const topCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const leftCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Local cursor state so a mouse-move doesn't bubble React
  // re-renders up to the editor. Subscribed once on mount.
  const [cursorScene, setCursorScene] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    return subscribeToCursorMove((x, y) => setCursorScene({ x, y }));
  }, [subscribeToCursorMove]);

  // Drag-from-ruler state. When `dragAxis` is non-null, the user
  // is dragging out a new guide; current screen-pixel cursor
  // position is held so we can render a preview line. Released
  // via the window-level mouseup listener below.
  const [drag, setDrag] = useState<{
    axis: 'horizontal' | 'vertical';
    /** The pointer that started the drag — events from any other
     *  pointer (a second finger / palm) are ignored so multi-touch
     *  can't move or commit the spawn at the wrong position. */
    pointerId: number;
    screenX: number;
    screenY: number;
  } | null>(null);

  // Redraw rulers whenever the viewport or cursor moves. Both
  // canvases share the same render closure; we just dispatch by
  // axis.
  useEffect(() => {
    drawRuler('horizontal', topCanvasRef.current, viewport, cursorScene);
    drawRuler('vertical', leftCanvasRef.current, viewport, cursorScene);
  }, [viewport, cursorScene]);

  const onTopPointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    setDrag({ axis: 'horizontal', pointerId: e.pointerId, screenX: e.clientX, screenY: e.clientY });
  }, []);
  const onLeftPointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    setDrag({ axis: 'vertical', pointerId: e.pointerId, screenX: e.clientX, screenY: e.clientY });
  }, []);

  // Window-level move/up listeners are the only way to capture
  // the drag once the cursor leaves the source ruler — without
  // this the drag would silently end on the first frame the
  // cursor crossed onto the Fabric canvas.
  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== drag.pointerId) return;
      setDrag((prev) => (prev ? { ...prev, screenX: e.clientX, screenY: e.clientY } : null));
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== drag.pointerId) return;
      const wrapper = topCanvasRef.current?.parentElement;
      const rect = wrapper?.getBoundingClientRect();
      if (rect) {
        // Drop position in wrapper-local coords. Project to
        // scene-space via the inverse of the viewport transform:
        //   scene = (screen - pan) / zoom
        // Skip the spawn if the cursor was dropped over the
        // ruler chrome itself (canceled drop = back onto the
        // source rail).
        const localX = e.clientX - rect.left;
        const localY = e.clientY - rect.top;
        const onRulerChrome = localX < RULER_THICKNESS || localY < RULER_THICKNESS;
        if (!onRulerChrome && viewport.zoom > 0) {
          const sceneX = (localX - RULER_THICKNESS - viewport.panX) / viewport.zoom;
          const sceneY = (localY - RULER_THICKNESS - viewport.panY) / viewport.zoom;
          if (drag.axis === 'horizontal') onSpawnGuide('horizontal', sceneY);
          else onSpawnGuide('vertical', sceneX);
        }
      }
      setDrag(null);
    };
    // A system-canceled gesture must ABORT — drop the drag without
    // spawning a phantom guide at the cancel position.
    const onCancel = (e: PointerEvent) => {
      if (e.pointerId !== drag.pointerId) return;
      setDrag(null);
    };
    // Pointer events (not mouse) so dragging a guide out of the ruler
    // works on touch — synthetic mouse events get canceled mid-drag once
    // the browser suspects a scroll. Paired with `touch-action: none` on
    // the ruler canvases below.
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [drag, viewport, onSpawnGuide]);

  // The ruler chrome is rendered INSIDE the canvas wrapper —
  // absolute-positioned to overlay the top + left edges. The
  // small corner box at the top-left covers the intersection
  // (where ticks from both rulers would otherwise overlap).
  const dragCursor =
    drag?.axis === 'horizontal' ? 'ns-resize' : drag?.axis === 'vertical' ? 'ew-resize' : 'default';

  return (
    <>
      {/* Top ruler */}
      <canvas
        ref={topCanvasRef}
        width={viewport.viewportWidth}
        height={RULER_THICKNESS}
        className="absolute left-0 top-0 z-10 select-none"
        style={{
          width: viewport.viewportWidth,
          height: RULER_THICKNESS,
          cursor: drag ? dragCursor : 'ns-resize',
          touchAction: 'none',
        }}
        onPointerDown={onTopPointerDown}
        aria-hidden="true"
      />
      {/* Left ruler */}
      <canvas
        ref={leftCanvasRef}
        width={RULER_THICKNESS}
        height={viewport.viewportHeight}
        className="absolute left-0 top-0 z-10 select-none"
        style={{
          width: RULER_THICKNESS,
          height: viewport.viewportHeight,
          cursor: drag ? dragCursor : 'ew-resize',
          touchAction: 'none',
        }}
        onPointerDown={onLeftPointerDown}
        aria-hidden="true"
      />
      {/* Corner cover at the intersection. Without this, the two
          rulers' tick rendering overlaps in the top-left
          RULER_THICKNESS × RULER_THICKNESS square. */}
      <div
        className="absolute left-0 top-0 z-10 bg-background border-r border-b border-border"
        style={{ width: RULER_THICKNESS, height: RULER_THICKNESS }}
        aria-hidden="true"
      />
      {/* Drag preview line — thin dashed indicator following the
          cursor while dragging out a new guide. */}
      {drag && <DragPreview drag={drag} wrapperRef={topCanvasRef.current?.parentElement ?? null} />}
    </>
  );
}

function DragPreview({
  drag,
  wrapperRef,
}: {
  drag: { axis: 'horizontal' | 'vertical'; screenX: number; screenY: number };
  wrapperRef: HTMLElement | null;
}) {
  if (!wrapperRef) return null;
  const rect = wrapperRef.getBoundingClientRect();
  const localX = drag.screenX - rect.left;
  const localY = drag.screenY - rect.top;
  // Don't show preview while still over the source ruler (looks
  // jittery + provides no info).
  if (localX < RULER_THICKNESS && drag.axis === 'vertical') return null;
  if (localY < RULER_THICKNESS && drag.axis === 'horizontal') return null;
  const lineStyle: React.CSSProperties = {
    position: 'absolute',
    pointerEvents: 'none',
    zIndex: 15,
    background: '#06b6d4',
    opacity: 0.6,
  };
  if (drag.axis === 'horizontal') {
    return (
      <div
        style={{
          ...lineStyle,
          left: RULER_THICKNESS,
          top: localY,
          right: 0,
          height: 1,
        }}
      />
    );
  }
  return (
    <div
      style={{
        ...lineStyle,
        top: RULER_THICKNESS,
        left: localX,
        bottom: 0,
        width: 1,
      }}
    />
  );
}

/** Draw one ruler axis into its backing canvas. Pulled out of
 *  the component so the rendering is straight-line procedural
 *  with no React identity concerns. */
function drawRuler(
  axis: 'horizontal' | 'vertical',
  canvas: HTMLCanvasElement | null,
  viewport: ViewportInfo,
  cursorScene: { x: number; y: number } | null,
): void {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  // Background + border.
  ctx.fillStyle = '#1e1e1e';
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = '#3a3a3a';
  ctx.lineWidth = 1;
  ctx.beginPath();
  if (axis === 'horizontal') {
    ctx.moveTo(0, h - 0.5);
    ctx.lineTo(w, h - 0.5);
  } else {
    ctx.moveTo(w - 0.5, 0);
    ctx.lineTo(w - 0.5, h);
  }
  ctx.stroke();

  if (viewport.zoom <= 0) return;
  const { major, minor } = pickTickSpacing(viewport.zoom);
  // The active drawing area starts AFTER the corner cover —
  // ruler tick zero is offset by `RULER_THICKNESS` so it lines
  // up with the canvas content below / right.
  const drawableSpan = axis === 'horizontal' ? w - RULER_THICKNESS : h - RULER_THICKNESS;
  const panOnAxis = axis === 'horizontal' ? viewport.panX : viewport.panY;
  const { min, max } = visibleSceneRange(panOnAxis, drawableSpan, viewport.zoom);

  ctx.strokeStyle = '#7a7a7a';
  ctx.fillStyle = '#bdbdbd';
  ctx.font = '10px ui-sans-serif, system-ui';
  ctx.textAlign = axis === 'horizontal' ? 'left' : 'right';
  ctx.textBaseline = axis === 'horizontal' ? 'top' : 'bottom';

  // Minor ticks.
  ctx.beginPath();
  for (const v of tickPositions(min, max, minor)) {
    const screenOffset = v * viewport.zoom + panOnAxis + RULER_THICKNESS;
    if (axis === 'horizontal') {
      ctx.moveTo(screenOffset + 0.5, h - 6);
      ctx.lineTo(screenOffset + 0.5, h);
    } else {
      ctx.moveTo(w - 6, screenOffset + 0.5);
      ctx.lineTo(w, screenOffset + 0.5);
    }
  }
  ctx.stroke();

  // Major ticks + labels.
  ctx.strokeStyle = '#c0c0c0';
  ctx.beginPath();
  for (const v of tickPositions(min, max, major)) {
    const screenOffset = v * viewport.zoom + panOnAxis + RULER_THICKNESS;
    if (axis === 'horizontal') {
      ctx.moveTo(screenOffset + 0.5, h - 12);
      ctx.lineTo(screenOffset + 0.5, h);
    } else {
      ctx.moveTo(w - 12, screenOffset + 0.5);
      ctx.lineTo(w, screenOffset + 0.5);
    }
  }
  ctx.stroke();

  for (const v of tickPositions(min, max, major)) {
    const screenOffset = v * viewport.zoom + panOnAxis + RULER_THICKNESS;
    const label = formatLabel(v, major);
    if (axis === 'horizontal') {
      ctx.fillText(label, screenOffset + 2, 2);
    } else {
      ctx.save();
      ctx.translate(w - 2, screenOffset - 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(label, 0, 0);
      ctx.restore();
    }
  }

  // Cursor tracking line — thin highlight at the cursor's
  // scene-space position on this axis.
  if (cursorScene) {
    const cursorScene1d = axis === 'horizontal' ? cursorScene.x : cursorScene.y;
    const cursorScreen = cursorScene1d * viewport.zoom + panOnAxis + RULER_THICKNESS;
    if (cursorScreen >= RULER_THICKNESS && cursorScreen <= (axis === 'horizontal' ? w : h)) {
      ctx.strokeStyle = '#fb7185';
      ctx.beginPath();
      if (axis === 'horizontal') {
        ctx.moveTo(cursorScreen + 0.5, 0);
        ctx.lineTo(cursorScreen + 0.5, h);
      } else {
        ctx.moveTo(0, cursorScreen + 0.5);
        ctx.lineTo(w, cursorScreen + 0.5);
      }
      ctx.stroke();
    }
  }
}

/** Format a scene-space ruler label. For integer-ish ticks (the
 *  common case), drop the trailing `.0`. Sub-pixel ticks (high
 *  zoom showing 0.2 / 0.4 / ...) keep one decimal. */
function formatLabel(value: number, major: number): string {
  if (major >= 1) return Math.round(value).toString();
  return value.toFixed(1);
}
